import React, { useEffect, useState } from "react";
import Input from "sushiweb/InputTypes/Input";
import Button from "sushiweb/Button";
import Textarea from "sushiweb/InputTypes/Textarea";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import {
  isValidated,
  getReadableDateTime,
  getInitialState,
  filterObj,
  isValNull,
} from "../../../utils";
import * as CityActions from "../../../store/actions/City";
import * as StateActions from "../../../store/actions/State";
import * as UserActions from "../../../store/actions/User";
import InfiniteScrollDropdown from "../../Common/InfiniteScrollDropdown";
import Permission from "../../Common/Permission";
import MenuNames from "../../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../../constants/Menu/MenuActionsMappings";
import NoFooterModal from "../../Common/NoFooterModal";
import { BottomSectionModal } from "../../../constants/assets/containers";
import variables from "../../../constants/assets/variables";
import PaginatedReactDropdown from "../../Common/PaginatedReactDropdown";

const citySupportedOptions = [
  { key: 1, label: "Yes" },
  { key: 0, label: "No" },
];

const mandatoryFields = [
  "city_name",
  "code",
  "city_supported",
  "state_id",
  "language_id",
];
const editableFields = [
  "city_name",
  "city_geo_bound",
  "driver_centre_address",
  "city_supported",
];

const CityFormModal = (props) => {
  const allActions = MenuActionsMappings[MenuNames.CITY];
  const [initialState, setInitialState] = useState({
    city_name: "",
    created_at: "",
    updated_at: "",
    city_geo_bound: "",
    city_supported: "",
    code: "",
    driver_centre_address: "",
    state_id: "",
    language_id: "",
  });
  const {
    modalState,
    handleClose,
    activeId,
    activeCity,
    activeFilters,
    filterFields,
    currentPage,
    setStatus,
  } = props;

  const handleSubmit = (evt) => {
    evt.preventDefault();

    // if filter button is clicked, then apply filters
    if (modalState === "Filter") {
      const filters = {};
      for (let key of filterFields) {
        if (initialState[key]) {
          filters[key] = initialState[key];
        }
      }
      props.getCityList(filters, {}).then((res) => {
        handleClose();
      });
    } else {
      // send different requests for Edit and Add state.
      if (modalState === "Edit") {
        const updatedEditableFields = [...editableFields];
        if (!props.allMenuActions[allActions.EDIT_CITY_SUPPORTED]) {
          updatedEditableFields.splice(
            updatedEditableFields.indexOf("city_supported"),
            1
          );
        }
        const body = filterObj(initialState, updatedEditableFields);
        props.updateCity(activeId, body).then((res) => {
          const pageRequest = { page: currentPage };
          props.getCityList(activeFilters, pageRequest);
          handleClose();
        });
      } else if (modalState === "Add") {
        const body = filterObj(initialState, [
          "city_name",
          "city_geo_bound",
          "city_supported",
          "code",
          "driver_centre_address",
          "state_id",
          "language_id",
        ]);
        if (body["city_supported"]) {
          body["city_supported"] = Boolean(body["city_supported"]);
        }
        let validatedState = isValidated(body, mandatoryFields);

        // language_id should be a number.
        const num = Number(body.language_id);
        if (isNaN(num) === true) {
          validatedState = "Language ID is not correct.";
        } else {
          body.language_id = num;
        }

        if (validatedState === "validated") {
          props.createCity(body).then((res) => {
            const pageRequest = { page: currentPage };
            props.getCityList(activeFilters, pageRequest);
            handleClose();
          });
        } else {
          setStatus({ type: "error", message: validatedState });
        }
      }
    }
  };

  // clears all applied filters.
  const handleClearFilters = () => {
    props.getCityList({}).then((res) => {
      props.resetState({ state: "activeFilters" });
      handleClose();
    });
  };

  const handleChange = (key, val) => {
    const values = { ...initialState };
    values[key] = val;
    if (key === "city_supported") {
      values[key] = Boolean(val) + "";
    }
    setInitialState(values);
  };

  useEffect(() => {
    if (activeId) {
      props.getCity(activeId);
    }

    // reset the state to show empty modal
    if (modalState === "Add" && Object.keys(activeCity).length > 0) {
      props.resetState({ state: "activeCity" });
    }
  }, [activeId, modalState]);

  useEffect(() => {
    // update the initialState on modal render.
    // this is not needed when modalState is "Add".
    if (modalState === "Edit" && Object.keys(activeCity).length > 0) {
      const values = { ...initialState };
      Object.keys(values).forEach((key) => {
        values[key] = !isValNull(activeCity[key])
          ? activeCity[key]
          : getInitialState(initialState, key);
      });
      if (!isValNull(values["city_supported"])) {
        values["city_supported"] = values["city_supported"] + "";
      }
      setInitialState(values);
    } else if (
      Object.keys(activeFilters).length > 0 &&
      modalState === "Filter"
    ) {
      const values = { ...initialState };
      Object.keys(values).forEach((key) => {
        values[key] = activeFilters[key]
          ? activeFilters[key]
          : getInitialState(initialState, key);
      });
      if (!isValNull(values["city_supported"])) {
        values["city_supported"] = values["city_supported"] + "";
      }
      setInitialState(values);
    }
  }, [activeCity, activeFilters]);

  // Returns true/false based on should the fields be disabled
  // on different states of modal.
  const isDisabledField = (value) => {
    if (modalState === "Edit") {
      if (editableFields.includes(value)) {
        if (value === "city_supported") {
          return !props.allMenuActions[allActions.EDIT_CITY_SUPPORTED];
        }
        return false;
      }
      return true;
    } else if (modalState === "Filter") {
      if (filterFields.includes(value)) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  const getRequiredButtonPerms = () => {
    if (modalState === "Edit") {
      return [allActions.UPDATE];
    } else if (modalState === "Add") {
      return [allActions.CREATE];
    }
    return undefined;
  };

  const isHiddenField = (key) => {
    if (modalState === "Filter") {
      return !filterFields.includes(key);
    } else if (modalState === "Add") {
      if (key === "created_at" || key === "updated_at") {
        return true;
      }
      return false;
    } else {
      return false;
    }
  };

  const getCitySupportedValue = (val) => {
    const activeItem = citySupportedOptions.find((item) => item.key === val);
    return activeItem ? activeItem.label : "";
  };

  const formElements = [
    {
      key: "city_name",
      element: (
        <Input
          value={initialState.city_name}
          disabled={isDisabledField("city_name")}
          data-testid="cityName"
          id="sushi-input"
          onChange={(e) => handleChange("city_name", e)}
          label="Name"
        />
      ),
    },
    {
      key: "city_geo_bound",
      element: (
        <Input
          value={initialState.city_geo_bound}
          disabled={isDisabledField("city_geo_bound")}
          data-testid="cityGeoBound"
          id="sushi-input"
          onChange={(e) => handleChange("city_geo_bound", e)}
          label="City Geo Bound"
        />
      ),
    },
    {
      key: "city_supported",
      element: (
        <PaginatedReactDropdown
          options={citySupportedOptions}
          labelKey="label"
          valueKey="key"
          data-testid="citySupported"
          id="citySupportedDropdown"
          value={
            initialState.city_supported !== "" &&
            getCitySupportedValue(
              Number(initialState.city_supported === "true" ? true : false)
            )
          }
          labelWidth="20%"
          disabled={isDisabledField("city_supported")}
          handleChange={handleChange}
          keyToChange="city_supported"
          placeholder="City Supported"
          showCross={false}
        />
      ),
    },
    {
      key: "code",
      element: (
        <Input
          value={initialState.code}
          disabled={isDisabledField("code")}
          data-testid="cityCode"
          id="sushi-input"
          onChange={(e) => handleChange("code", e)}
          label="Code"
        />
      ),
    },
    {
      key: "driver_centre_address",
      element: (
        <Textarea
          id="sushi-textarea"
          data-testid="cityDriverCenterAddress"
          borderColor={variables.primaryColor}
          innerWidth="100%"
          className="mb-20"
          resize="none"
          label="Driver Centre Address"
          disabled={isDisabledField("driver_centre_address")}
          onChange={(e) => handleChange("driver_centre_address", e)}
          value={initialState.driver_centre_address}
        />
      ),
    },
    {
      key: "language_id",
      element: (
        <Input
          value={initialState.language_id}
          disabled={isDisabledField("language_id")}
          data-testid="cityLanguageId"
          id="sushi-input"
          onChange={(e) => handleChange("language_id", e)}
          label="Language ID"
        />
      ),
    },
    {
      key: "state_id",
      element: (
        <InfiniteScrollDropdown
          id="cityStateDropdown"
          priorityFlag
          getActiveList={props.getStateList}
          nextPage={props.nextPageState}
          resKey="states"
          modalState={modalState}
          keyName="state_id"
          labelKey="name"
          getDataLabelKey="state.name"
          keyTypeToSet="id"
          disabled={isDisabledField("state_id")}
          label="State"
          handleChange={handleChange}
          remoteSearchApplicable
          activeFilters={activeFilters}
          activeElement={activeCity}
          getActiveItemById={props.getState}
          appliedFilters={{}}
          remoteSearchKey="startswith"
        />
      ),
    },
  ];

  return (
    <NoFooterModal
      header={true}
      heading={`${modalState} City`}
      handleClose={handleClose}
      id="CityFormModal"
      modalButtons={
        <BottomSectionModal className="flex items-center justify-between">
          {/* show the clear button when clicked on filters button */}
          {modalState === "Filter" && (
            <Button
              data-testid="filterFeatureBtn"
              onClick={handleClearFilters}
              className="sushi-btn"
            >
              Clear
            </Button>
          )}
          <Permission requiredPerms={getRequiredButtonPerms()}>
            <Button
              data-testid="submitButton"
              className="sushi-btn"
              onClick={handleSubmit}
            >
              {modalState === "Filter" ? "Apply" : "Submit"}
            </Button>
          </Permission>
        </BottomSectionModal>
      }
    >
      {formElements
        .filter((obj) => !isHiddenField(obj.key))
        .map((obj) => obj.element)}
      {modalState === "Edit" ? (
        <>
          <Input
            value={getReadableDateTime(initialState.created_at)}
            disabled
            data-testid="cityCreatedAt"
            id="sushi-input"
            onChange={(e) => handleChange("created_at", e)}
            label="Created At"
          />
          <Input
            value={getReadableDateTime(initialState.updated_at)}
            disabled
            data-testid="cityUpdatedAt"
            id="sushi-input"
            onChange={(e) => handleChange("updated_at", e)}
            label="Updated A"
          />
        </>
      ) : (
        ""
      )}
    </NoFooterModal>
  );
};

const mapStateToProps = (state) => {
  const { City, State, MenuActions } = state;
  return {
    activeCity: City.activeCity,
    activeFilters: City.activeFilters,
    currentPage: City.currentPage,
    nextPageState: State.nextPage,
    allMenuActions: MenuActions.allMenuActions,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    createCity: bindActionCreators(CityActions.createCity, dispatch),
    getCityList: bindActionCreators(CityActions.getCityList, dispatch),
    getCity: bindActionCreators(CityActions.getCity, dispatch),
    resetState: bindActionCreators(CityActions.resetState, dispatch),
    updateCity: bindActionCreators(CityActions.updateCity, dispatch),
    getStateList: bindActionCreators(StateActions.getStateList, dispatch),
    getState: bindActionCreators(StateActions.getState, dispatch),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CityFormModal);
